import { useCallback, useEffect, useState } from "react";
import { CHECK_TYPES, DEFAULT_STATE } from "./DatasetCheckScreen.constants";
import { checkDatasetValidationService, getDatasetOutliersCompletenessService } from "services/chats";
import { useDispatch, useSelector } from "react-redux";
import { SET_SHOW_VALIDATION } from "actions/chats";

export const useDatasetCheckScreen = ({
  show,
  dataset,
  validationData,
  onSuccessValidationRefresh,
  datasetLoading,
}) => {
  const dispatch = useDispatch();
  const { dataset_id: id } = useSelector((state) => state.chatsReducer.activeChat);
  const { name } = useSelector((state) => state.chatsReducer.activeDataset);
  const { showValidation } = useSelector((state) => state.chatsReducer) || {};

  const dataset_id = dataset?.id || id;
  const datasetName = dataset?.name || name;
  const showValidationStatus = show || showValidation;

  const [checkState, setCheckState] = useState(DEFAULT_STATE);
  const [showDetails, setShowDetails] = useState(null);
  const [showDatasetInfo, setShowDatasetInfo] = useState(true);
  const [validationDataset, setValidationDataset] = useState({
    data: {},
    loading: true,
    score: 0,
  });

  const getDatasetValidations = useCallback(() => {
    if (!dataset_id || validationData || datasetLoading) return;
    checkDatasetValidationService(dataset_id)
      .then((res) => {
        if (!res.data) return;
        const { general_scores, validation_dict } = res.data || {};

        setValidationDataset({
          data: validation_dict,
          loading: false,
          score: general_scores?.validity || 0,
        });
      })
      .catch(() =>
        setValidationDataset({
          data: {},
          loading: false,
          score: 0,
        })
      );
  }, [dataset_id, validationData, datasetLoading]);

  const onContinueClick = () => {
    dispatch({ type: SET_SHOW_VALIDATION, showValidation: false });

    setValidationDataset({
      data: {},
      loading: false,
      score: 0,
    });
    setCheckState(DEFAULT_STATE);
  };

  const getDatasetOutliersCompleteness = useCallback(() => {
    if (!dataset_id) return;

    getDatasetOutliersCompletenessService(dataset_id)
      .then((res) => {
        if (res.data) {
          const { general_scores, outliers_dict, completeness_dict } = res.data || {};

          setCheckState({
            ...checkState,
            [CHECK_TYPES[1].name]: {
              data: completeness_dict || {},
              loading: false,
              score: general_scores?.[CHECK_TYPES[1].name] || 0,
            },
            [CHECK_TYPES[2].name]: {
              data: outliers_dict || {},
              loading: false,
              score: general_scores?.[CHECK_TYPES[2].name] || 0,
            },
          });
        }
        if (!validationData && !datasetLoading) getDatasetValidations();
      })
      .catch(() =>
        setCheckState({
          ...checkState,
          [CHECK_TYPES[1].name]: {
            data: {},
            loading: false,
            score: 0,
          },
          [CHECK_TYPES[2].name]: {
            data: {},
            loading: false,
            score: 0,
          },
        })
      );
  }, [dataset_id, checkState, getDatasetValidations, validationData, datasetLoading]);

  useEffect(() => {
    if (!validationData) return;
    const { general_scores, validation_dict } = validationData || {};

    setValidationDataset({
      data: validation_dict,
      loading: false,
      score: general_scores?.validity || 0,
    });
    onSuccessValidationRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationData]);

  useEffect(() => {
    if (!showValidationStatus || !dataset_id || !show) return;
    setShowDatasetInfo(true);
    setShowDetails(null);
    getDatasetOutliersCompleteness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showValidationStatus, dataset_id, show]);

  return {
    checkState: { ...checkState, [CHECK_TYPES[0].name]: validationDataset },
    setShowDetails,
    showDetails,
    showDatasetInfo,
    setShowDatasetInfo,
    datasetName,
    onContinueClick,
  };
};
